import { initializeApp } from 'firebase/app';
import * as firestore from 'firebase/firestore';
import * as auth from 'firebase/auth';
import * as functions from 'firebase/functions';
import { UserAuthInfo, UserCredentials } from '../../types/auth';
import { FirestoreNextPageConfig, FirestorePreviuosPageConfig } from '../../types/common';
import { FirestoreQueryConstraint } from '../../types/firebase';

const FIRESTORE_EMULATOR_ON = false as const;

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
}

const firebaseApp = initializeApp(firebaseConfig);

const firebaseAuth = auth.getAuth(firebaseApp);
const firestoreDB = firestore.getFirestore(firebaseApp);
const firebaseFunctions = functions.getFunctions(firebaseApp);

if (FIRESTORE_EMULATOR_ON) {
  firestore.connectFirestoreEmulator(firestoreDB, 'localhost', 8080);
  auth.connectAuthEmulator(firebaseAuth, 'http://localhost:9099');
  functions.connectFunctionsEmulator(firebaseFunctions, 'localhost', 5001);
}

/* AUTH */
export function getCurrentUser(): UserAuthInfo | null {
  return firebaseAuth.currentUser;
}

export function onAuthStateChanged(
  callback: (userAuthInfo: UserAuthInfo | null) => void,
): auth.Unsubscribe {
  return firebaseAuth.onAuthStateChanged(callback);
}

export async function createUserWithEmailAndPassword(
  credentials: UserCredentials
): Promise<auth.UserCredential> {
  const { email, password } = credentials;
  return auth.createUserWithEmailAndPassword(firebaseAuth, email, password);
}

export function signInEmailAndPassword(
 credentials: UserCredentials
): Promise<auth.UserCredential> {
  const { email, password } = credentials;
  return auth.signInWithEmailAndPassword(firebaseAuth, email, password);
}

export function signOut(): Promise<void> {
  return firebaseAuth.signOut();
}

export async function reauthUser(
  user: UserAuthInfo,
  email: string,
  password: string,
): Promise<auth.UserCredential> {
  const credentials = auth.EmailAuthProvider.credential(email, password);
  return auth.reauthenticateWithCredential(user, credentials);
}

export async function authUpdatePassword(
  user: UserAuthInfo,
  password: string
): Promise<void> {
  return auth.updatePassword(user, password);
}

/* FIRESTORE */
export function firestoreCollectionRef(
  collection: string
): firestore.CollectionReference<firestore.DocumentData, firestore.DocumentData> {
  return firestore.collection(firestoreDB, collection);
}

export function firestoreDocRef(
  collection: string,
  document: string
): firestore.DocumentReference<firestore.DocumentData, firestore.DocumentData> {
  return firestore.doc(firestoreDB, collection, document);
}

export function firestoreDocAdd(
  collection: string,
  data: any
): Promise<firestore.DocumentReference<any, firestore.DocumentData>> {
  const collectionRef = firestoreCollectionRef(collection);
  return firestore.addDoc(collectionRef, data);
}

export function firestoreNewDocRef(
  collection: string
): firestore.DocumentReference<firestore.DocumentData, firestore.DocumentData> {
  return firestore.doc(firestore.collection(firestoreDB, collection));
}

export function firestoreGetCountFromServer(
  collection: string,
  queryConstraints?: FirestoreQueryConstraint[]
): Promise<firestore.AggregateQuerySnapshot<{
  count: firestore.AggregateField<number>;
}, firestore.DocumentData, firestore.DocumentData>> {
  const collectionRef = firestoreCollectionRef(collection);
  if (queryConstraints) {
    const query = firestore.query(collectionRef, ...queryConstraints);
    return firestore.getCountFromServer(query);
  } else {
    return firestore.getCountFromServer(collectionRef);
  }
}

export function firestoreDocDelete(
  collection: string,
  doc: string
): Promise<void> {
  const docRef = firestoreDocRef(collection, doc);
  return firestore.deleteDoc(docRef);
}

export function firestoreDocRead(
  collection: string,
  document: string,
): Promise<firestore.DocumentSnapshot<firestore.DocumentData, firestore.DocumentData>> {
  const docRef = firestoreDocRef(collection, document);
  return firestore.getDoc(docRef);
}

export function firestoreDocUpdate(
  collection: string,
  document: string,
  data: any
): Promise<void> {
  const docRef = firestoreDocRef(collection, document);
  return firestore.updateDoc(docRef, data);
}

export function firestoreWrite(
  collection: string,
  doc: string,
  data: any,
): Promise<void> {
  const docRef = firestoreDocRef(collection, doc);
  return firestore.setDoc(docRef, data);
}

export function firestoreCollectionRead(
  collection: string
): Promise<firestore.QuerySnapshot<firestore.DocumentData, firestore.DocumentData>> {
  const collectionRef = firestoreCollectionRef(collection);
  return firestore.getDocs(collectionRef);
}

export function firestoreBatch(): firestore.WriteBatch {
  return firestore.writeBatch(firestoreDB);
}

export function firestoreOnSnapshot(
  collection: string,
  doc: string,
  callback: (doc: firestore.DocumentSnapshot<firestore.DocumentData, firestore.DocumentData>) => void
): firestore.Unsubscribe {
  const docRef = firestoreDocRef(collection, doc);
  return firestore.onSnapshot(docRef, callback);
}

export async function firestoreGetNextPage(
  collection: string,
  paginationConfig: FirestoreNextPageConfig,
  orderBy: string
): Promise<firestore.QuerySnapshot<firestore.DocumentData, firestore.DocumentData>> {
  //const collectionRef = firestoreCollectionRef(collection);
  const { pageSize, lastDoc } = paginationConfig;
  const queryConstraints: FirestoreQueryConstraint[] = [];
  queryConstraints.push(firestoreOrderBy(orderBy));
  if (lastDoc) queryConstraints.push(firestoreStartAfter(lastDoc));
  queryConstraints.push(firestoreLimit(pageSize));
  const query = firestoreBuildQuery(collection, ...queryConstraints);
  return firestoreExecuteQuery(query);
  /* if (lastDoc) {
    const query = firestore.query(collectionRef, firestore.orderBy(orderBy), firestore.startAfter(lastDoc), firestore.limit(pageSize));
    return firestore.getDocs(query);
  } else {
    const query = firestore.query(collectionRef, firestore.orderBy(orderBy), firestore.limit(pageSize));
    return firestore.getDocs(query);
  } */
}

export async function firestoreGetPreviousPage(
  collection: string,
  paginationConfig: FirestorePreviuosPageConfig,
  orderBy: string
): Promise<firestore.QuerySnapshot<firestore.DocumentData, firestore.DocumentData>> {
  const collectionRef = firestoreCollectionRef(collection);
  const { firstDoc, pageSize } = paginationConfig;
  const query = firestore.query(collectionRef, firestore.orderBy(orderBy), firestore.endBefore(firstDoc), firestore.limitToLast(pageSize));
  return firestore.getDocs(query);
}

export async function firestoreRefreshPage(
  collection: string,
  cursor: firestore.QueryDocumentSnapshot<firestore.DocumentData>,
  size: number,
  orderBy: string,
) {
  const collectionRef = firestoreCollectionRef(collection);
  const query = firestore.query(collectionRef, firestore.orderBy(orderBy), firestore.startAt(cursor), firestore.limit(size));
  return firestore.getDocs(query);
}

export async function firestoreElasticSearch(
  collection: string,
  field: string,
  string: string
): Promise<firestore.QuerySnapshot<firestore.DocumentData, firestore.DocumentData>> {
  const collectionRef = firestoreCollectionRef(collection);
  const query = firestore.query(collectionRef, firestore.orderBy(field), firestore.startAt(string), firestore.endAt(string + '\uf8ff'));
  return firestore.getDocs(query);
}

/* export async function testFilter(
  collection: string
) {
  const collectionRef = firestoreCollectionRef(collection);
  const query = firestore.query(
    collectionRef, 
    firestore.orderBy('metadata.normalizedSurname'), 
    firestore.startAt('tr'), 
    firestore.endAt('tr' + '\uf8ff'), 
    firestore.where('metadata.keywords', 'array-contains-any', ['and']),
    firestore.where('personalData.telephone', '==', '0123456789'),
    firestore.where('personalData.height', '==', 167),
    firestore.where('personalData.weight', '==', 67),
    firestore.where('personalData.address', '==', 'Via Roma, 6')
  );
  return firestore.getDocs(query);
} */

export function firestoreBuildQuery(
  collection: string,
  ...queryConstraints: (firestore.QueryNonFilterConstraint | firestore.QueryFieldFilterConstraint)[]
) {
  const collectionRef = firestoreCollectionRef(collection);
  return firestore.query(collectionRef, ...queryConstraints);
}  

export async function firestoreExecuteQuery(
  query: firestore.Query<firestore.DocumentData, firestore.DocumentData>
): Promise<firestore.QuerySnapshot<firestore.DocumentData, firestore.DocumentData>> {
  return firestore.getDocs(query);
}

/* FIRESTORE QUERY UTILS */
export function firestoreOrderBy(
  filed: string,
  direction?: firestore.OrderByDirection
): firestore.QueryOrderByConstraint {
  return firestore.orderBy(filed, direction)
}

export function firestoreStartAt(
  val: any
): firestore.QueryStartAtConstraint {
  return firestore.startAt(val);
} 

export function firestoreStartAfter(
  val: any
): firestore.QueryStartAtConstraint {
  return firestore.startAfter(val);
} 

export function firestoreLimit(
  val: number
): firestore.QueryLimitConstraint {
  return firestore.limit(val);
}

export function firestoreLimitToLast(
  val: number
): firestore.QueryLimitConstraint {
  return firestore.limitToLast(val);
}

export function firestoreEndAt(
  val: any
): firestore.QueryEndAtConstraint {
  return firestore.endAt(val);
}

export function firestoreEndBefore(
  val: any
): firestore.QueryEndAtConstraint {
  return firestore.endBefore(val);
} 

export function firestoreWhere(
  field: string,
  condition: firestore.WhereFilterOp,
  value: any
): firestore.QueryFieldFilterConstraint {
  return firestore.where(field, condition, value);
}

/* FUNCTIONS */
export async function callCloudFunction(
  name: string,
  payload: any
): Promise<functions.HttpsCallableResult<unknown>> {
  const cloudFunction = functions.httpsCallable(firebaseFunctions, name);
  return cloudFunction(payload);
}